import React from 'react';
import { Button } from 'antd';
import { Trans } from 'react-i18next';
import i18next from 'i18next';

import apiClient from '../../api/apiClient';
import Mixpanel from '../../analytics/mixpanel';
import notificationService from '@utils/notificationService';
import TaskSentMessage from '@shared/Components/TaskSentMessage';
import { SubdivisionTypeLabel } from '@utils/locationSubdivisions';

export const START_REQUEST = 'START_QUERIES_REQUEST';
export const STOP_REQUEST = 'STOP_QUERIES_REQUEST';
export const FETCH_LOCATIONS_SUCCESS = 'FETCH_LOCATIONS_SUCCESS';
export const START_TASK_VALIDATION = 'START_TASK_VALIDATION';
export const VALIDATE_TASK_SUCCESS = 'VALIDATE_TASK_SUCCESS';

const TASK_TOP_UP_ERROR_MESSAGES = new Set([
  'Please add credits or wait till the current tasks are finished before creating more task.',
  'Please add credits to create this task.',
  'Oops, your account got suspended. Just add credits to create tasks.',
]);

const TOP_UP_URL = '/billing/payments?secondary_tab=true';

export const startRequest = () => ({ type: START_REQUEST });
export const stopRequest = () => ({ type: STOP_REQUEST });

function transformData(data, parent) {
  return data.map(node => ({ title: (node.t || node.v), value: parent + '>' + node.v, children: node.c ? transformData(node.c, parent + '>' + node.v) : undefined }));
}

export const sendExtractionTask = (task) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiClient.post('/tasks', task);

    notificationService.success({
      message: i18next.t('message.sent'),
      description: <TaskSentMessage />,
      duration: 8, style: { width: 600 }
    });

    // `false` is a successful non-first task; `null` is reserved for failures.
    return Boolean(data.is_first_task);
  } catch (error) {
    const notificationConfig = { message: error.message, duration: 8 };

    if (TASK_TOP_UP_ERROR_MESSAGES.has(error.message?.trim())) {
      notificationConfig.actions = (
        <Button
          type='primary'
          href={TOP_UP_URL}
          target='_blank'
          rel='noopener noreferrer'
        >
          <Trans i18nKey='title.addCredits' />
        </Button>
      );
    }

    notificationService.error(notificationConfig);
    return null;
  } finally {
    dispatch(stopRequest());
  }
};

export const validateExtractionTask = (task) => async (dispatch, getState) => {
  const validationRequestId = (getState().queriesReducer.validationRequestId || 0) + 1;
  dispatch({ type: START_TASK_VALIDATION, validationRequestId });
  // An abandoned request must not replace a newer quote or its task snapshot.
  const isCurrentRequest = () => getState().queriesReducer.validationRequestId === validationRequestId;
  try {
    const { data } = await apiClient.post('/validate_task', task);
    if (!isCurrentRequest()) return;
    dispatch({ taskValidationResult: data, type: VALIDATE_TASK_SUCCESS });
    return data;
  } catch (error) {
    if (!isCurrentRequest()) return;
    notificationService.error({ message: error.message });
  } finally {
    if (isCurrentRequest()) dispatch(stopRequest());
  }

  Mixpanel.track('Validate task', { task });
};

export const fetchLocations = (country) => async (dispatch, getState) => {
  try {
    dispatch(startRequest());

    const { countryLocations, countrySupportsZipCodes, countrySubdivisionTypes = {} } = getState().queriesReducer;
    if (country in countryLocations && Object.prototype.hasOwnProperty.call(countrySubdivisionTypes, country)) {
      return countryLocations[country];
    }

    const { data } = await apiClient.get('/locations', { params: { country, v: 3 } });
    const subdivisionType = data.subdivision_type === undefined
      ? (data.supports_zip_codes ? 'postal_code' : null)
      : data.subdivision_type;
    dispatch({
      countryLocations: { ...countryLocations, [country]: labelZipCodes(transformData(data.items, country), country, subdivisionType) },
      countrySupportsZipCodes: { ...countrySupportsZipCodes, [country]: data.supports_zip_codes },
      countrySubdivisionTypes: { ...getState().queriesReducer.countrySubdivisionTypes, [country]: subdivisionType },
      type: FETCH_LOCATIONS_SUCCESS
    });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
};

function labelZipCodes(treeData, country, subdivisionType) {
  const translatedLabel = <SubdivisionTypeLabel subdivisionType={subdivisionType} country={country} />;

  return treeData.map((state) => {
    return {
      ...state,
      children: state.children.map((city) => {
        if (city.children && city.children.every((zip) => !zip.children)) {
          return {
            ...city,
            children: city.children.map((zip) => ({
              ...zip,
              title: <>{zip.title} <span className='tag lowercase'>{translatedLabel}</span></>,
            })),
          };
        }
        return city;
      }),
    };
  });
}
